// B76: current-database store. Holds the session's selected database and
// drives `USE <db>` over the shared singleton WebSocket so every query on that
// connection (query console, schema browser, …) resolves tables in the chosen
// database. The choice is persisted in-module so a transparent reconnect
// (socket.js auto-reconnect) re-applies it — a fresh connection always starts
// at the default database.

import { wsSql, onSocketOpen } from './socket.js';

export const DEFAULT_DB = 'default';

let _current = DEFAULT_DB;
const _listeners = new Set();

function notify() {
  for (const cb of _listeners) {
    try { cb(_current); } catch { /* listener error */ }
  }
}

export function getCurrentDb() { return _current; }

export function subscribeCurrentDb(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

// Quote a database identifier for SQL. The parser's `expect_db_name` accepts a
// bare identifier or a double-quoted name; simple names go bare, anything else
// is double-quoted (with embedded quotes doubled).
function quoteDbIdent(name) {
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) return name;
  return '"' + String(name).replace(/"/g, '""') + '"';
}

// Switch the session's current database. Sends `USE <db>` on the shared WS so
// all subsequent queries on that connection resolve in `name`. Throws (with
// the server's message) if the database does not exist — the caller surfaces
// it and the selection is left unchanged.
export async function setCurrentDb(name) {
  await wsSql(`USE ${quoteDbIdent(name)}`);
  if (_current !== name) { _current = name; notify(); }
}

// SHOW DATABASES → sorted list of names (default + every registered database).
export async function listDatabases() {
  const r = await wsSql('SHOW DATABASES');
  return (r.data || []).map((row) => row[0]);
}

export async function createDatabase(name) {
  await wsSql(`CREATE DATABASE ${quoteDbIdent(name)}`);
}

// Drop a database. If it is the current one, fall back to the default first so
// the session isn't left pointing at a gone database.
export async function dropDatabase(name) {
  if (name === DEFAULT_DB) throw new Error('the default database cannot be dropped');
  if (_current === name) await setCurrentDb(DEFAULT_DB);
  await wsSql(`DROP DATABASE ${quoteDbIdent(name)}`);
}

// Re-apply the selected database after a transparent reconnect — a brand-new
// connection defaults to `default`, silently losing a prior `USE`.
onSocketOpen(() => {
  if (_current !== DEFAULT_DB) {
    wsSql(`USE ${quoteDbIdent(_current)}`).catch(() => { /* re-apply best-effort */ });
  }
});
